import { supabase } from '@supabaseClient';
import { convertToAccountCurrency } from '@lib/currencyConversion/accountCurrency.ts';
import { captureSentryException } from '@/integrations/sentry/client';

type LinkedSaccoTable = 'sacco_shares' | 'sacco_withdrawals' | 'sacco_deposits';

/**
 * Converts an edited SACCO amount into the currency of the account its linked transaction posts to.
 *
 * Returns null when the row has no linked transaction, or no recorded currency (rows created before
 * the currency was stored); the update RPC then posts the entered amount as is.
 */
export async function convertLinkedSaccoAmount(
  userId: string,
  table: LinkedSaccoTable,
  id: string,
  amount: number
): Promise<number | null> {
  try {
    const { data, error } = await supabase
      .from(table as 'sacco_shares')
      .select('currency, transactions(account_id)')
      .eq('id', id)
      .single();
    if (error) throw error;

    const row = data as unknown as { currency: string | null; transactions: { account_id: string } | null };
    if (!row.currency || !row.transactions) return null;

    const { convertedAmount } = await convertToAccountCurrency(
      userId, row.transactions.account_id, amount, 0, row.currency
    );
    return convertedAmount;
  } catch (error) {
    captureSentryException(error as Error, undefined, `Failed to convert the edited amount of ${table} ${id}`);
    throw error;
  }
}
